import React from 'react';
import { Input, Select, DatePicker, InputNumber } from 'antd';
import dayjs from 'dayjs';
import LookupField from '../../../components/LookupField';
import { FG160, SEND_TYPE_MAP, INVOICE_TYPE_MAP, DOC_TYPE_MAP, STATUS_MAP } from '../../../services/pwg208Api';

const { Option } = Select;

interface Tab1MainProps {
  formData: FG160;
  isEditing: boolean;
  getField: <K extends keyof FG160>(key: K) => FG160[K];
  setField: <K extends keyof FG160>(key: K, value: FG160[K]) => void;
  onLookupConfirm?: (field: string, code: string) => void;
}

const Tab1Main: React.FC<Tab1MainProps> = React.memo(({ formData, isEditing, getField, setField, onLookupConfirm }) => {
  const statusInfo = STATUS_MAP[formData.fg160_80] ?? STATUS_MAP['A'];

  const toDateVal = (v: string | null) =>
    v ? dayjs(v) : null;

  const fromDateVal = (val: dayjs.Dayjs | null): string | null =>
    val ? val.format('YYYY-MM-DD') + 'T00:00:00Z' : null;

  return (
    <div className="pwg208-tab1">
      {/* Row 1: 申請單號 / 狀態 / 申請日期 */}
      <div className="form-row">
        <div className="form-item">
          <label className="form-label" data-contract-field="fg160_01">申請單號</label>
          <Input
            value={formData.fg160_01 || '(新增後產生)'}
            disabled
            className="field-code"
          />
        </div>
        <div className="form-item">
          <label className="form-label" data-contract-field="fg160_80">狀態</label>
          <span
            className="status-badge"
            style={{ color: statusInfo.color === 'error' ? '#ff4d4f' : undefined }}
          >
            {statusInfo.label}
          </span>
        </div>
        <div className="form-item">
          <label className="form-label" data-contract-field="fg160_11">申請日期</label>
          <DatePicker
            value={toDateVal(formData.fg160_11 as string | null)}
            onChange={(val) => setField('fg160_11', fromDateVal(val))}
            disabled={!isEditing}
            format="YYYY-MM-DD"
            style={{ width: '100%' }}
          />
        </div>
      </div>

      {/* Row 2: 交運單號 / 單據類別 / 部門 */}
      <div className="form-row">
        <div className="form-item">
          <label className="form-label" data-contract-field="fg160_16">交運單號</label>
          <Input
            value={String(getField('fg160_16'))}
            disabled
            maxLength={7}
          />
        </div>
        <div className="form-item">
          <label className="form-label" data-contract-field="fg160_24">單據類別</label>
          <div className="lookup-inline">
            <Input
              value={String(getField('fg160_24'))}
              disabled
              style={{ width: 60 }}
            />
            <span className="lookup-name">
              {DOC_TYPE_MAP[formData.fg160_24] ?? ''}
            </span>
          </div>
        </div>
        <div className="form-item">
          <label className="form-label" data-contract-field="fg160_17">部門代號</label>
          <Input
            value={String(getField('fg160_17'))}
            disabled
            maxLength={4}
          />
        </div>
      </div>

      {/* Row 3: 業務員 / 下單客戶 / 幣別 */}
      <div className="form-row">
        <div className="form-item">
          <label className="form-label" data-contract-field="fg160_12">業務員</label>
          <Input
            value={String(getField('fg160_12'))}
            disabled
            maxLength={8}
          />
        </div>
        <div className="form-item">
          <label className="form-label" data-contract-field="fg160_18">下單客戶</label>
          <Input
            value={String(getField('fg160_18'))}
            disabled
            maxLength={12}
          />
        </div>
        <div className="form-item">
          <label className="form-label" data-contract-field="fg160_19">交運幣別</label>
          <Input
            value={String(getField('fg160_19'))}
            disabled
            maxLength={3}
          />
        </div>
      </div>

      {/* Row 4: 開票對象 */}
      <div className="form-row">
        <div className="form-item form-item-wide">
          <label className="form-label required" data-contract-field="fg160_15">開票對象</label>
          <LookupField
            lookupType="customer"
            value={String(getField('fg160_15'))}
            onChange={(code) => setField('fg160_15', code)}
            onCodeConfirm={(code) => onLookupConfirm?.('fg160_15', code)}
            disabled={!isEditing}
            maxLength={80}
            style={{ width: '100%' }}
          />
        </div>
        <div className="form-item">
          <label className="form-label" data-contract-field="fg160_13">傳送方式</label>
          <Select
            value={formData.fg160_13}
            onChange={(val) => setField('fg160_13', val)}
            disabled={!isEditing}
            style={{ width: '100%' }}
          >
            {Object.entries(SEND_TYPE_MAP).map(([k, v]) => (
              <Option key={k} value={Number(k)}>{v}</Option>
            ))}
          </Select>
        </div>
      </div>

      {/* Row 5: 發票種類 / 匯率 */}
      <div className="form-row">
        <div className="form-item form-item-wide">
          <label className="form-label" data-contract-field="fg160_14">發票種類</label>
          <Select
            value={formData.fg160_14}
            onChange={(val) => setField('fg160_14', val)}
            disabled={!isEditing}
            style={{ width: '100%' }}
          >
            {Object.entries(INVOICE_TYPE_MAP).map(([k, v]) => (
              <Option key={k} value={Number(k)}>{v}</Option>
            ))}
          </Select>
        </div>
        <div className="form-item">
          <label className="form-label" data-contract-field="fg160_20">交運匯率</label>
          <InputNumber
            value={formData.fg160_20}
            disabled
            style={{ width: '100%' }}
            precision={6}
          />
        </div>
        <div className="form-item">
          <label className="form-label" data-contract-field="fg160_21">發票匯率</label>
          <InputNumber
            value={formData.fg160_21}
            disabled
            style={{ width: '100%' }}
            precision={6}
          />
        </div>
      </div>

      {/* Row 6: 金額小計 */}
      <div className="form-row">
        <div className="form-item">
          <label className="form-label" data-contract-field="fg160_22">實際開票淨額</label>
          <InputNumber
            value={formData.fg160_22}
            disabled
            style={{ width: '100%' }}
            precision={2}
            formatter={(v) => v != null ? v.toLocaleString('en-US', { maximumFractionDigits: 2 }) : '0'}
          />
        </div>
        <div className="form-item">
          <label className="form-label" data-contract-field="fg160_23">實際開票總額</label>
          <InputNumber
            value={formData.fg160_23}
            disabled
            style={{ width: '100%' }}
            precision={2}
            formatter={(v) => v != null ? v.toLocaleString('en-US', { maximumFractionDigits: 2 }) : '0'}
          />
        </div>
        <div className="form-item">
          <label className="form-label" data-contract-field="fg160_27">稅額</label>
          <InputNumber
            value={formData.fg160_27}
            disabled
            style={{ width: '100%' }}
            precision={2}
          />
        </div>
        <div className="form-item">
          <label className="form-label" data-contract-field="fg160_25">開票數量</label>
          <InputNumber
            value={formData.fg160_25}
            disabled
            style={{ width: '100%' }}
            precision={2}
          />
        </div>
      </div>

      {/* Row 7: 報關單號 / 關聯單號 */}
      <div className="form-row">
        <div className="form-item">
          <label className="form-label" data-contract-field="fg160_30">報關單號</label>
          <Input
            value={String(getField('fg160_30'))}
            onChange={(e) => setField('fg160_30', e.target.value)}
            disabled={!isEditing}
            maxLength={20}
          />
        </div>
        <div className="form-item">
          <label className="form-label" data-contract-field="fg160_33">客訴/銷貨折讓單號</label>
          <Input
            value={String(getField('fg160_33'))}
            disabled
            maxLength={20}
          />
        </div>
      </div>

      {/* Row 8: 預收款 */}
      <div className="form-row">
        <div className="form-item">
          <label className="form-label" data-contract-field="fg160_28">預收款金額</label>
          <InputNumber
            value={formData.fg160_28}
            disabled
            style={{ width: '100%' }}
            precision={2}
          />
        </div>
        <div className="form-item">
          <label className="form-label" data-contract-field="fg160_29">預收款含稅</label>
          <InputNumber
            value={formData.fg160_29}
            disabled
            style={{ width: '100%' }}
            precision={2}
          />
        </div>
      </div>

      {/* Row 9: 建立資訊 */}
      <div className="form-row form-row-info">
        <span>建立人: {formData.fg160_91}</span>
        <span>建立日期: {formData.fg160_92 ? dayjs(formData.fg160_92).format('YYYY-MM-DD') : '-'}</span>
        <span>財務送審人: {formData.fg160_81}</span>
        <span>立單核准人: {formData.fg160_82}</span>
        <span>財務核准人: {formData.fg160_83}</span>
      </div>
    </div>
  );
});

Tab1Main.displayName = 'Tab1Main';

export default Tab1Main;
